import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { Company } from '../../../../../models/company.model';
import { CompanyService } from '../../../../../services/company.service';
import { SiloBag, SiloBagRef } from '../../models/silo-bag.model';
import { SiloBagService } from '../../services/silo-bag.service';
import { SILO_BAG_COVERAGE_MARGIN } from './silo-bag-selector.validators';

@Component({
  selector: 'app-silo-bag-selector',
  templateUrl: './silo-bag-selector.component.html',
  styleUrls: ['./silo-bag-selector.component.scss']
})
export class SiloBagSelectorComponent implements OnInit, OnChanges, OnDestroy {

  /** Tonelaje requerido por la orden ODD. */
  @Input() public orderQuantity: number = 0;
  /** Filtro opcional por tipo de grano (slug del producto). */
  @Input() public grainTypeFilter?: string;
  /** Cuando es true, deshabilita los checkboxes. */
  @Input() public disabled: boolean = false;
  /** Solo lectura: muestra las bolsas ya comprometidas (sin edición). */
  @Input() public readonly: boolean = false;
  /** SiloIDs preseleccionados (modo edición). */
  @Input() public committedIds: string[] = [];
  /**
   * Snapshot de las bolsas ya comprometidas (modo readonly). Viene del backend
   * (`negotiation.silo_bags`) — la contraparte las ve SIN llamar a Siloreal.
   */
  @Input() public committedSnapshot: SiloBagRef[] = [];
  /** True cuando las bolsas las comprometió la contraparte (ej. productor), no la empresa logueada. */
  @Input() public committedByCounterparty: boolean = false;
  /** En edición de contraorden: negociación a excluir del filtrado de comprometidas. */
  @Input() public excludeNegotiationId?: number;

  /** Emite el array de bolsas seleccionadas. */
  @Output() public selectionChange = new EventEmitter<SiloBag[]>();
  /** Emite true cuando la cobertura es válida (suma > requerido * 1.10). */
  @Output() public validityChange = new EventEmitter<boolean>();

  public bags: SiloBag[] = [];
  public selected: { [siloId: string]: boolean } = {};
  public loading: boolean = true;
  public loadError: boolean = false;
  /** True cuando el backend respondió 400: la empresa no tiene credenciales de Siloreal. */
  public missingCredentials: boolean = false;
  public readonly margin = SILO_BAG_COVERAGE_MARGIN;
  public currentPage: number = 1;
  public readonly pageSize: number = 10;

  private _searchTerm: string = '';
  public get searchTerm(): string { return this._searchTerm; }
  public set searchTerm(value: string) {
    this._searchTerm = value;
    this.currentPage = 1;
  }
  public readonly silorealAppUrl = 'https://app.siloreal.net/';

  private company: Company;
  private destroy$ = new Subject<void>();
  private wasValid: boolean = false;

  constructor(
    private companyService: CompanyService,
    private siloBagService: SiloBagService,
    private toastrService: ToastrService,
    private translateService: TranslateService
  ) { }

  ngOnInit(): void {
    // En modo readonly las bolsas comprometidas vienen del backend (snapshot persistido):
    // no se llama a Siloreal ni se necesita el listado de la empresa.
    if (this.readonly) {
      this.bags = this.bagsFromSnapshot();
      this.loading = false;
      this.emitValidity();
      return;
    }

    this.companyService.watch().pipe(takeUntil(this.destroy$)).subscribe(company => {
      if (!company) return;
      this.company = company;
      this.loadBags();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['committedSnapshot'] && this.readonly) {
      this.bags = this.bagsFromSnapshot();
      this.emitValidity();
    }
    if (changes['committedIds'] && this.bags.length) {
      this.applyCommittedSelection();
    }
    if ((changes['orderQuantity'] && !changes['orderQuantity'].firstChange) && this.bags.length) {
      // Recalcular la validez cuando cambia la cantidad requerida.
      this.emitValidity();
    }
  }

  /** Construye los SiloBag mostrados en modo readonly a partir del snapshot del backend. */
  private bagsFromSnapshot(): SiloBag[] {
    return (this.committedSnapshot ?? []).map(ref => {
      const baggedDate = ref.bagged_date ? ref.bagged_date.slice(0, 10) : null;
      return new SiloBag({
        silo_id:            ref.silobag_id,
        silobag_uuid:       ref.silobag_uuid ?? null,
        silobag_name:       ref.silobag_name ?? null,
        grain_type:         (ref.species ?? '').toString().toLowerCase(),
        harvest:            ref.harvest ?? null,
        organization:       ref.organization ?? null,
        input_weight:       this.refNumber(ref.input_weight),
        remaining_stock:    this.refNumber(ref.remaining_stock),
        // El snapshot guarda stock en kg (como Siloreal); la tabla muestra toneladas.
        bagged_weight:      (this.refNumber(ref.remaining_stock) ?? this.refNumber(ref.input_weight) ?? 0) / 1000,
        lot_name:           ref.lot_name ?? null,
        countryside_name:   ref.countryside_name ?? null,
        humidity:           this.refNumber(ref.humidity),
        bagged_date:        baggedDate,
        days_since_bagging: baggedDate ? this.daysSince(baggedDate) : 0,
        latitude:           this.refNumber(ref.latitude),
        longitude:          this.refNumber(ref.longitude),
      });
    });
  }

  private daysSince(dateStr: string): number {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return 0;
    const ms = Date.now() - new Date(year, month - 1, day).getTime();
    return Math.floor(ms / 86_400_000);
  }

  private refNumber(raw: number | string | null | undefined): number | null {
    if (raw == null || raw === '') return null;
    const parsed = typeof raw === 'number' ? raw : parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadBags(): void {
    this.loading = true;
    this.loadError = false;
    this.missingCredentials = false;
    this.siloBagService.listByCompany(this.company.id, this.excludeNegotiationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: bags => {
          this.bags = bags;
          this.applyCommittedSelection();
          this.loading = false;
          this.emitValidity();
        },
        error: (err: HttpErrorResponse) => {
          this.loading = false;
          // 400 = la empresa no tiene credenciales de Siloreal cargadas: hay que
          // indicarle al usuario que las configure, no es un error de conexión.
          this.missingCredentials = err?.status === 400;
          this.loadError = !this.missingCredentials;
          this.validityChange.emit(false);
        }
      });
  }

  /** Ruta a la pantalla de integraciones donde se cargan las credenciales de Siloreal. */
  public get integrationsLink(): string {
    return this.company ? `/company/${this.company.id}/integrations` : '';
  }

  /** Bolsas que se muestran en la tabla según el modo. */
  public get displayBags(): SiloBag[] {
    if (this.readonly) {
      return this.committedBags;
    }
    return this.availableBags;
  }

  /** Bolsas filtradas por el término de búsqueda (nombre y lote). */
  public get filteredDisplayBags(): SiloBag[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.displayBags;
    return this.displayBags.filter(bag =>
      (bag.silobag_name ?? '').toLowerCase().includes(term) ||
      (bag.lot_name ?? '').toLowerCase().includes(term) ||
      (bag.silo_id ?? '').toLowerCase().includes(term)
    );
  }

  public get totalPages(): number {
    return Math.ceil(this.filteredDisplayBags.length / this.pageSize);
  }

  public get paginatedBags(): SiloBag[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredDisplayBags.slice(start, start + this.pageSize);
  }

  public get pageNumbers(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const delta = 2;
    const left = Math.max(2, this.currentPage - delta);
    const right = Math.min(total - 1, this.currentPage + delta);
    const pages: number[] = [1];
    if (left > 2) pages.push(-1);
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push(-1);
    pages.push(total);
    return pages;
  }

  public goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  /**
   * Bolsas ya comprometidas para esta negociación. En modo readonly `this.bags` ya es el
   * snapshot del backend, así que se muestran todas. En modo edición se filtra por los
   * committedIds preseleccionados.
   */
  public get committedBags(): SiloBag[] {
    if (this.readonly) return this.bags;
    if (!this.committedIds?.length) return [];
    const ids = new Set(this.committedIds);
    return this.bags.filter(bag => ids.has(bag.silo_id));
  }

  /** Bolsas disponibles para asignar (no comprometidas con otra orden, y compatibles con el grano). */
  public get availableBags(): SiloBag[] {
    return this.bags.filter(bag => {
      if (bag.assigned_order_id != null) return false;
      if (this.grainTypeFilter && bag.grain_type !== this.grainTypeFilter) return false;
      return true;
    });
  }

  public get selectedBags(): SiloBag[] {
    if (this.readonly) {
      return this.committedBags;
    }
    return this.availableBags.filter(bag => this.selected[bag.silo_id]);
  }

  public get selectedTotal(): number {
    return this.selectedBags.reduce((sum, b) => sum + (Number(b.bagged_weight) || 0), 0);
  }

  public get requiredCoverage(): number {
    return (Number(this.orderQuantity) || 0) * this.margin;
  }

  public get hasValidCoverage(): boolean {
    return this.selectedTotal > this.requiredCoverage && this.selectedBags.length > 0;
  }

  public get committedHelpKey(): string {
    return this.committedByCounterparty
      ? 'SILO_BAGS.COMMITTED_HELP_COUNTERPARTY'
      : 'SILO_BAGS.COMMITTED_HELP';
  }

  private applyCommittedSelection(): void {
    this.selected = {};
    if (!this.committedIds?.length) return;
    this.committedIds.forEach(id => {
      this.selected[id] = true;
    });
  }

  public toggle(bag: SiloBag): void {
    if (this.disabled || this.readonly) return;
    this.selected[bag.silo_id] = !this.selected[bag.silo_id];
    this.selectionChange.emit(this.selectedBags);
    this.emitValidity();
  }

  public baggedDateValue(bag: SiloBag): Date | null {
    if (!bag.bagged_date) return null;
    const [year, month, day] = bag.bagged_date.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  public hasCoordinates(bag: SiloBag): boolean {
    return bag.latitude != null && bag.longitude != null;
  }

  public openInGoogleMaps(bag: SiloBag, event: Event): void {
    event.stopPropagation();
    if (!this.hasCoordinates(bag)) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${bag.latitude},${bag.longitude}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  private emitValidity(): void {
    if (this.readonly) {
      this.validityChange.emit(this.committedBags.length > 0);
      return;
    }
    const valid = this.hasValidCoverage;
    if (valid && !this.wasValid) {
      this.toastrService.success(this.translateService.instant('SILO_BAGS.COVERAGE_OK'));
    }
    this.wasValid = valid;
    this.validityChange.emit(valid);
  }
}
